// components/FiltrarMedios/FiltrarMediosModal.jsx
import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { filtrarMediosService } from "./FiltrarMediosService";

function FiltrarMediosModal(props) {
  const { 
    open: openProp, 
    onClose, 
    editingFiltro, 
    choferes, 
    medios, 
    onSuccess 
  } = props;

  const [open, setOpen] = useState(false);
  const [disBtn, setDisBtn] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [colormensaje, setColorMensaje] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [formData, setFormData] = useState({
    id_chofer: "",
    filtro_nombre: "",
    medios_incluidos: "",
  });
  const [selectedMedios, setSelectedMedios] = useState([]);
  const [selectedChofer, setSelectedChofer] = useState(null);
  const [verificandoFiltro, setVerificandoFiltro] = useState(false);
  const [tieneFiltro, setTieneFiltro] = useState(false);
  const [filtroExistente, setFiltroExistente] = useState(null);

  // Sincronizar el estado interno con la prop open
  useEffect(() => {
    setOpen(openProp);
    if (openProp) {
      setDisBtn(false);
      setTieneFiltro(false);
      setFiltroExistente(null);
    }
  }, [openProp]);

  // Cargar datos al editar / resetear el form cada vez que se abre el modal
  useEffect(() => {
    if (!openProp) return;

    if (editingFiltro) {
      // Buscar el chofer seleccionado
      const choferSeleccionado = choferes.find(
        (c) => c.id === editingFiltro.id_chofer
      );
      setSelectedChofer(choferSeleccionado || null);

      setFormData({
        id_chofer: editingFiltro.id_chofer || "",
        filtro_nombre: editingFiltro.filtro_nombre || "",
        medios_incluidos: editingFiltro.medios_incluidos || "",
      });

      if (editingFiltro.medios_incluidos) {
        const ids = editingFiltro.medios_incluidos
          .split(",")
          .map((id) => parseInt(id))
          .filter((id) => !isNaN(id));
        const seleccionados = medios.filter((m) => ids.includes(m.id));
        setSelectedMedios(seleccionados);
      } else {
        setSelectedMedios([]);
      }
      // En edición, no verificamos porque es el mismo filtro
      setTieneFiltro(false);
      setFiltroExistente(null);
    } else {
      setSelectedChofer(null);
      setFormData({
        id_chofer: "",
        filtro_nombre: "",
        medios_incluidos: "",
      });
      setSelectedMedios([]);
      setTieneFiltro(false);
      setFiltroExistente(null);
    }
    setDisBtn(false);
  }, [openProp, editingFiltro, choferes, medios]);

  // Verificar si el chofer tiene filtro al seleccionar uno
  useEffect(() => {
    const verificar = async () => {
      if (selectedChofer && !editingFiltro) {
        setVerificandoFiltro(true);
        try {
          const result = await filtrarMediosService.verificarFiltro(selectedChofer.id);
          setTieneFiltro(result.tieneFiltro);
          setFiltroExistente(result.filtro);
        } catch (error) {
          console.error('Error verificando filtro:', error);
          setTieneFiltro(false);
          setFiltroExistente(null);
        } finally {
          setVerificandoFiltro(false);
        }
      } else {
        setTieneFiltro(false);
        setFiltroExistente(null);
      }
    };

    verificar();
  }, [selectedChofer, editingFiltro]);

  // Snackbar Notificacion
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const handleClose = () => {
    setOpen(false);
    setDisBtn(false);
    if (onClose) {
      onClose();
    }
  };

  const almacenar = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleChoferChange = (event, newValue) => {
    setSelectedChofer(newValue);
    setFormData({
      ...formData,
      id_chofer: newValue ? newValue.id : "",
    });
  };

  const guardarFiltro = async (e) => {
    e.preventDefault();
    setDisBtn(true);

    // Validar campos requeridos
    if (!formData.id_chofer) {
      setOpenSnack(true);
      setColorMensaje("#FADBD8");
      setMensaje("Por favor seleccione un chofer");
      setDisBtn(false);
      return;
    }

    if (!formData.filtro_nombre) {
      setOpenSnack(true);
      setColorMensaje("#FADBD8");
      setMensaje("Por favor ingrese un nombre para el filtro");
      setDisBtn(false);
      return;
    }

    if (selectedMedios.length === 0) {
      setOpenSnack(true);
      setColorMensaje("#FADBD8");
      setMensaje("Por favor seleccione al menos un medio de pago");
      setDisBtn(false);
      return;
    }

    // Verificar si el chofer ya tiene un filtro (solo en creación)
    if (!editingFiltro && tieneFiltro) {
      const nombreChofer = selectedChofer?.nombre_apellido || "Este chofer";
      setOpenSnack(true);
      setColorMensaje("#FADBD8");
      setMensaje(`${nombreChofer} ya tiene un filtro asignado: "${filtroExistente?.filtro_nombre}". Solo puede tener un filtro por chofer.`);
      setDisBtn(false);
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        medios_incluidos: selectedMedios.map((m) => m.id).join(","),
      };

      if (editingFiltro) {
        await filtrarMediosService.update(editingFiltro.id, dataToSend);
        setOpenSnack(true);
        setColorMensaje("#D4EFDF");
        setMensaje("El filtro se actualizó exitosamente!");
      } else {
        await filtrarMediosService.create(dataToSend);
        setOpenSnack(true);
        setColorMensaje("#D4EFDF");
        setMensaje("El filtro se guardó exitosamente!");
      }

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("Error guardando filtro:", error);
      setOpenSnack(true);
      setColorMensaje("#FADBD8");
      setMensaje("Error al guardar el filtro");
      setDisBtn(false);
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={() => handleClose()}
        maxWidth="md"
      >
        <Box justifyContent="center" backgroundColor="#1F618D">
          <DialogTitle>
            <Typography fontSize={20} align="center" color="#ffffff">
              {editingFiltro ? "Editar Filtro" : "Nuevo Filtro de Medios"}
            </Typography>
          </DialogTitle>
        </Box>
        <form autoComplete="off" onSubmit={guardarFiltro}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} py={1} mb={1}>
                <Autocomplete
                  options={choferes}
                  getOptionLabel={(option) => 
                    `${option.nombre_apellido}`
                  }
                  value={selectedChofer}
                  onChange={handleChoferChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Chofer"
                      placeholder="Buscar chofer..."
                      required
                      size="small"
                      focused
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {verificandoFiltro && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => 
                    option.id === value.id
                  }
                  noOptionsText="No se encontraron choferes"
                  fullWidth
                  disabled={!!editingFiltro}
                />
                {editingFiltro && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    * No se puede cambiar el chofer en edición
                  </Typography>
                )}
                {selectedChofer && !editingFiltro && verificandoFiltro && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Verificando si el chofer tiene filtro...
                    </Typography>
                  </Alert>
                )}
                {selectedChofer && !editingFiltro && !verificandoFiltro && tieneFiltro && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Atención:</strong> {selectedChofer.nombre_apellido} ya tiene un filtro asignado.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Filtro actual:</strong> {filtroExistente?.filtro_nombre}
                    </Typography>
                    
                  </Alert>
                )}
                {selectedChofer && !editingFiltro && !verificandoFiltro && !tieneFiltro && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Disponible:</strong> {selectedChofer.nombre_apellido} no tiene filtro asignado.
                    </Typography>
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} py={1} mb={1}>
                <TextField
                  label="Nombre del Filtro"
                  name="filtro_nombre"
                  size="small"
                  placeholder="Ej: solo_efectivo, sin_tarjetas"
                  inputProps={{
                    maxLength: 100,
                  }}
                  focused
                  required
                  fullWidth
                  value={formData.filtro_nombre}
                  onChange={almacenar}
                />
              </Grid>

              <Grid item xs={12} py={1} mb={1}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Medios de Pago Permitidos *</InputLabel>
                  <Select
                    multiple
                    value={selectedMedios.map((m) => m.id)}
                    onChange={(e) => {
                      const selectedIds = e.target.value;
                      const selected = medios.filter((m) =>
                        selectedIds.includes(m.id)
                      );
                      setSelectedMedios(selected);
                    }}
                    label="Medios de Pago Permitidos *"
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const medio = medios.find((m) => m.id === id);
                          return (
                            <Chip
                              key={id}
                              label={medio ? medio.nombre : `ID ${id}`}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {medios.map((medio) => (
                      <MenuItem key={medio.id} value={medio.id}>
                        {medio.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
              </Grid>

            </Grid>

            <Grid
              container
              direction="row"
              justifyContent="space-between"
              mt={2}
            >
              <Grid>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => handleClose()}
                  style={{ backgroundColor: "#fff" }}
                >
                  Volver
                </Button>
              </Grid>
              <Grid>
                <Button
                  type="submit"
                  color="success"
                  variant="contained"
                  disabled={
                    disBtn || 
                    (selectedChofer && !editingFiltro && tieneFiltro) ||
                    verificandoFiltro
                  }
                >
                  {editingFiltro ? "Actualizar" : "Guardar"}
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </form>
      </Dialog>

      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          open={openSnack}
          autoHideDuration={1500}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnack}
            severity={colormensaje === "#D4EFDF" ? "success" : "error"}
            sx={{ width: "100%" }}
            style={{ backgroundColor: colormensaje, color: "black" }}
          >
            {mensaje}
          </Alert>
        </Snackbar>
      </Stack>
    </>
  );
}

export default FiltrarMediosModal;